import {
  collectPhotoDisplayItemsFromFlat,
  wizardFormDataAsFlat,
} from './serviceWizardFormData';

/** City + state for display; `street` / `zip` are not shown. */
function formatLocationSummary(address) {
  if (!address?.city?.trim()) return '';
  const city = address.city.trim();
  const st = (address.state || 'KY').trim();
  return st ? `${city}, ${st}` : city;
}

function findLabel(options, id) {
  return options?.find((o) => o.id === id)?.label ?? '—';
}

/**
 * Review rows for a saved search request, using the per-trade question configs
 * (`constants/mockData/*SubserviceFlows.js`) to turn stored answer ids into labels.
 * Used to show saved requests (account page, admin waitlist, contact sheet summary).
 *
 * @param {object} flowConfig trade flow config from `wizardServiceRegistry`
 * @param {import('./serviceWizardFormData').ServiceWizardFormData} formData
 * @returns {{ rows: Array<{ label: string, value: string }>, title: string, subtitle: string, photoItems: Array<{ src: string, name: string }> }}
 */
export function buildWizardReviewPresentation(flowConfig, formData) {
  const empty = { rows: [], title: 'Your request', subtitle: '', photoItems: [] };
  if (!flowConfig || !formData) return empty;
  const flat = wizardFormDataAsFlat(flowConfig, formData);
  if (!flat) return empty;

  const { problemStep, urgencyStep, reviewStep } = flowConfig;
  const problemLabel = findLabel(problemStep?.options, flat.problem);
  const urgencyLabel = findLabel(urgencyStep?.options, flat.urgency);
  const reviewRows = [{ label: 'Problem', value: problemLabel }];

  const branchList = flowConfig.flowBranches?.[flat.problem];
  if (branchList) {
    branchList.forEach((b) => {
      if (b.condition && !b.condition(flat)) return;
      const val = flat[b.key];
      if (val) {
        reviewRows.push({
          label: b.title.replace(/[?]/g, '').trim(),
          value: findLabel(b.options, val),
        });
      }
    });
  }

  reviewRows.push({ label: 'Urgency', value: urgencyLabel });
  if (flat.details) reviewRows.push({ label: 'Details', value: flat.details });
  const photoItems = collectPhotoDisplayItemsFromFlat(flat.photos);
  if (photoItems.length > 0) {
    const n = photoItems.length;
    reviewRows.push({
      label: 'Photos',
      value: `${n} photo${n === 1 ? '' : 's'}`,
    });
  }
  reviewRows.push({
    label: 'City',
    value: formatLocationSummary(flat.address) || '—',
  });

  return {
    rows: reviewRows,
    title: reviewStep?.title ?? 'Review your request',
    subtitle: reviewStep?.subtitle ?? '',
    photoItems,
  };
}
